/* PDP Redo — pooled family rating, read from the Inflate review app.
 *
 * WHY THIS EXISTS
 * ---------------
 * The obvious source for a rating in Liquid is product.metafields.reviews.rating
 * / .rating_count. On this store those are empty for most products: Inflate holds
 * the reviews and never synced them into Shopify metafields. Camilla, for
 * instance, has 17 reviews across its family in Inflate and nothing at all in the
 * metafields. So the rating has to be read from Inflate at runtime.
 *
 * The block still renders a Liquid rating when the metafields DO have data (Mia
 * had some), so this script is an upgrade rather than the only path. When it
 * resolves, it overwrites whatever Liquid produced, because Inflate is the more
 * complete source.
 *
 * POOLING
 * -------
 * The family is resolved server-side (first word of the product title, matched
 * across the bar-stools and dining-chairs collections) and handed over as
 * data-family-ids. Each id is asked for its aggregate and the results are
 * combined as a count-weighted mean:
 *
 *     pooled = SUM(average_rating_i * total_reviews_i) / SUM(total_reviews_i)
 *
 * Verified safe to sum: the sibling review sets are disjoint. Two Mia stools both
 * report 4.93 from 15 reviews, which looks like double counting, but their review
 * ids share nothing — it is a coincidence.
 *
 * Results are cached in sessionStorage so moving between siblings in the family
 * (which the style selector encourages) does not re-request the same aggregates.
 */
(function () {
  'use strict';

  var CACHE_PREFIX = 'pdp-redo-reviews:';
  var CACHE_TTL_MS = 30 * 60 * 1000;
  var FALLBACK_BASE = 'https://inflate-back.osc-fr1.scalingo.io';

  /* Inflate's backend + public widget key are already in the page: on
     window.__INFLATE_STARS_CONFIG once its widget boots, and before that in the
     query string of its own script tags. Read them rather than hardcoding, so a
     key rotation does not silently blank the rating. */
  function inflateConfig() {
    var cfg = window.__INFLATE_STARS_CONFIG || window.__INFLATE || {};
    var base = cfg.backend || null;
    var key = null;

    var scripts = document.getElementsByTagName('script');
    for (var i = 0; i < scripts.length; i++) {
      var src = scripts[i].src;
      if (!src || src.indexOf('api_key=') === -1) continue;
      var k = src.match(/api_key=([0-9a-zA-Z-]{16,})/);
      if (k) key = k[1];
      if (!base) {
        var b = src.match(/base_url=([^&]+)/);
        if (b) {
          try { base = decodeURIComponent(b[1]); } catch (e) { /* leave base null */ }
        }
      }
      if (key) break;
    }

    if (!key) return null;
    return { base: base || FALLBACK_BASE, key: key };
  }

  function cacheGet(cacheKey) {
    try {
      var raw = sessionStorage.getItem(CACHE_PREFIX + cacheKey);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || Date.now() - parsed.at > CACHE_TTL_MS) return null;
      return parsed.value;
    } catch (e) {
      return null; // private mode, blocked storage, corrupt entry
    }
  }

  function cacheSet(cacheKey, value) {
    try {
      sessionStorage.setItem(CACHE_PREFIX + cacheKey, JSON.stringify({ at: Date.now(), value: value }));
    } catch (e) { /* non-fatal */ }
  }

  function fetchAggregate(cfg, productId) {
    var url = cfg.base + '/product_reviews/aggregate/?api_key=' + encodeURIComponent(cfg.key) +
              '&product_id=' + encodeURIComponent(productId);
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        if (!d) return null;
        var avg = Number(d.average_rating);
        var count = Number(d.total_reviews);
        if (!isFinite(avg) || !isFinite(count) || count <= 0) return null;
        return { avg: avg, count: count };
      })
      .catch(function () { return null; }); // one dead sibling must not kill the row
  }

  function pool(results) {
    var weighted = 0;
    var total = 0;
    results.forEach(function (r) {
      if (!r) return;
      weighted += r.avg * r.count;
      total += r.count;
    });
    if (total <= 0) return null;
    return { rating: weighted / total, count: total };
  }

  function render(root, pooled) {
    var starsEl = root.querySelector('[data-pdp-redo-stars]');
    var valueEl = root.querySelector('[data-pdp-redo-value]');
    var countEl = root.querySelector('[data-pdp-redo-count]');
    if (!starsEl || !valueEl || !countEl) return;

    var symbolId = root.getAttribute('data-star-symbol');
    var halfId = root.getAttribute('data-star-half');
    var scaleMax = Number(root.getAttribute('data-scale-max')) || 5;
    var outline = root.getAttribute('data-stars-style') === 'outline';

    var rounded = Math.round(pooled.rating * 10) / 10;

    /* Same half-star rule as the Liquid path: a remainder between .3 and .7
       renders a half star, above .7 rounds the star up. */
    var whole = Math.floor(rounded);
    var remainder = rounded - whole;
    var half = remainder >= 0.3 && remainder <= 0.7;
    var filled = whole + (remainder > 0.7 ? 1 : half ? 1 : 0);

    var svgNs = 'http://www.w3.org/2000/svg';
    var xlinkNs = 'http://www.w3.org/1999/xlink';
    starsEl.textContent = '';

    for (var i = 1; i <= scaleMax; i++) {
      var svg = document.createElementNS(svgNs, 'svg');
      svg.setAttribute('class', 'pdp-redo-rating__star' + (i <= filled ? ' is-filled' : ''));
      svg.setAttribute('viewBox', '0 0 32 32');
      svg.setAttribute('role', 'presentation');
      svg.setAttribute('aria-hidden', 'true');

      var use = document.createElementNS(svgNs, 'use');
      use.setAttributeNS(xlinkNs, 'xlink:href', '#' + symbolId);
      if (half && i === filled) use.setAttribute('fill', 'url(#' + halfId + ')');
      svg.appendChild(use);

      if (outline) {
        var stroke = document.createElementNS(svgNs, 'use');
        stroke.setAttributeNS(xlinkNs, 'xlink:href', '#' + symbolId);
        stroke.setAttribute('fill', 'none');
        stroke.setAttribute('stroke', 'var(--pdp-redo-star-fill)');
        stroke.setAttribute('stroke-width', 'var(--icon-stroke-width)');
        svg.appendChild(stroke);
      }

      starsEl.appendChild(svg);
    }

    starsEl.setAttribute('aria-label', rounded + ' out of ' + scaleMax + ' stars');
    valueEl.textContent = rounded.toFixed(1);
    countEl.textContent = '(' + pooled.count + ')';
    root.hidden = false;

    var link = root.closest('.pdp-redo-rating-link');
    if (link) {
      link.hidden = false;
      link.setAttribute('aria-label', rounded.toFixed(1) + ' out of ' + scaleMax +
        ' stars from ' + pooled.count + ' reviews. Read the reviews.');
    }
  }

  function run(root) {
    var ids = (root.getAttribute('data-family-ids') || '')
      .split(',')
      .map(function (s) { return s.trim(); })
      .filter(Boolean);
    if (!ids.length) return;

    var cacheKey = ids.slice().sort().join('-');
    var cached = cacheGet(cacheKey);
    if (cached) { render(root, cached); return; }

    var cfg = inflateConfig();
    if (!cfg) return; // Inflate not on the page: leave whatever Liquid rendered

    Promise.all(ids.map(function (id) { return fetchAggregate(cfg, id); }))
      .then(function (results) {
        var pooled = pool(results);
        if (!pooled) return;
        cacheSet(cacheKey, pooled);
        render(root, pooled);
      });
  }

  function start() {
    var roots = document.querySelectorAll('[data-pdp-redo-rating]');
    for (var i = 0; i < roots.length; i++) run(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
